import React, { useState, useEffect } from 'react';
import { subscriptionsApi, calendarFeedApi, CalendarFeedInfo } from '../services/api';
import { User } from '../types';

interface SubscriptionManagerProps {
  onClose: () => void;
  onSubscriptionsChange?: () => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ onClose, onSubscriptionsChange }) => {
  const [subscriptions, setSubscriptions] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [feedInfo, setFeedInfo] = useState<CalendarFeedInfo | null>(null);
  const [subscribeToAll, setSubscribeToAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedFeedUrl, setCopiedFeedUrl] = useState(false);
  const [updatingSubscribeToAll, setUpdatingSubscribeToAll] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [subs, available, feed, subscribeToAllStatus] = await Promise.all([
        subscriptionsApi.getAll(),
        subscriptionsApi.getAvailable(),
        calendarFeedApi.getFeedUrl(),
        subscriptionsApi.getSubscribeToAllStatus(),
      ]);
      setSubscriptions(subs);
      setAvailableUsers(available);
      setFeedInfo(feed);
      setSubscribeToAll(subscribeToAllStatus.subscribeToAll);
    } catch (err: any) {
      console.error('Failed to load subscriptions:', err);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeToAllToggle = async () => {
    setUpdatingSubscribeToAll(true);
    try {
      const result = await subscriptionsApi.setSubscribeToAll(!subscribeToAll);
      setSubscribeToAll(result.subscribeToAll);
      if (result.subscribeToAll && result.subscribedCount > 0) {
        alert(`Subscribed to ${result.subscribedCount} new user(s)!`);
      }
      await loadData();
      onSubscriptionsChange?.();
    } catch (err: any) {
      console.error('Failed to update subscribe-to-all:', err);
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setUpdatingSubscribeToAll(false);
    }
  };

  const handleSubscribe = async (userId: string) => {
    try {
      await subscriptionsApi.subscribe(userId);
      await loadData();
      onSubscriptionsChange?.();
    } catch (err: any) {
      console.error('Failed to subscribe:', err);
      alert(err.response?.data?.message || 'Failed to subscribe');
    }
  };

  const handleUnsubscribe = async (userId: string) => {
    try {
      await subscriptionsApi.unsubscribe(userId);
      await loadData();
      onSubscriptionsChange?.();
    } catch (err: any) {
      console.error('Failed to unsubscribe:', err);
      alert(err.response?.data?.message || 'Failed to unsubscribe');
    }
  };

  const copyFeedUrl = () => {
    if (feedInfo?.feedUrl) {
      navigator.clipboard.writeText(feedInfo.feedUrl);
      setCopiedFeedUrl(true);
      setTimeout(() => setCopiedFeedUrl(false), 2000);
    }
  };

  const handleRegenerateToken = async () => {
    if (!confirm('This will invalidate your current feed URL. Any calendars using the old URL will stop syncing. Continue?')) {
      return;
    }
    try {
      const newFeed = await calendarFeedApi.regenerateToken();
      setFeedInfo(newFeed);
      alert('Feed URL regenerated successfully!');
    } catch (err: any) {
      console.error('Failed to regenerate token:', err);
      alert('Failed to regenerate feed URL');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-slate-800">Manage Subscriptions</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Subscribe to other users to see their reservations on your calendar.
        </p>

        {/* Subscribe to All Toggle */}
        {!loading && !error && (
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-purple-800">Subscribe to All Users</h4>
                <p className="text-sm text-purple-600 mt-1">
                  Automatically subscribe to all current and future users. New users will be added automatically.
                </p>
              </div>
              <button
                onClick={handleSubscribeToAllToggle}
                disabled={updatingSubscribeToAll}
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  subscribeToAll ? 'bg-purple-600' : 'bg-gray-300'
                } ${updatingSubscribeToAll ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    subscribeToAll ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {subscribeToAll && (
              <p className="text-xs text-purple-500 mt-2">
                You're subscribed to all users. New users will be automatically added to your subscriptions.
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadData}
              className="mt-2 text-blue-600 hover:text-blue-700"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* Current Subscriptions */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Currently Subscribed ({subscriptions.length})
              </h4>
              {subscriptions.length === 0 ? (
                <p className="text-gray-500 text-sm py-2">
                  You haven't subscribed to anyone yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {subscriptions.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium mr-3">
                          {user.firstName?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnsubscribe(user.id)}
                        className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        Unsubscribe
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Users */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Available to Subscribe ({availableUsers.length})
              </h4>
              {availableUsers.length === 0 ? (
                <p className="text-gray-500 text-sm py-2">
                  {subscriptions.length > 0
                    ? "You're subscribed to all available users."
                    : 'No other users available to subscribe to.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {availableUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-sm font-medium mr-3">
                          {user.firstName?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleSubscribe(user.id)}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Subscribe
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* iCal Feed Section */}
            {feedInfo && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Sync to External Calendar
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  Add this URL to Google Calendar, Outlook, or any calendar app that supports iCal subscriptions.
                  Your reservations and subscribed users' reservations will sync automatically.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="text"
                      readOnly
                      value={feedInfo.feedUrl}
                      className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-700 font-mono"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      onClick={copyFeedUrl}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        copiedFeedUrl
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {copiedFeedUrl ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  <div className="text-xs text-gray-500 space-y-2">
                    <p><strong>Google Calendar:</strong> Settings &gt; Add calendar &gt; From URL &gt; paste the URL</p>
                    <p><strong>Outlook:</strong> Add calendar &gt; Subscribe from web &gt; paste the URL</p>
                    <p><strong>Apple Calendar:</strong> File &gt; New Calendar Subscription &gt; paste the URL</p>
                  </div>

                  <button
                    onClick={handleRegenerateToken}
                    className="mt-3 text-xs text-red-600 hover:text-red-700 underline"
                  >
                    Regenerate URL (invalidates old URL)
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;
